/**
 * Générateur — Comprendre le sens d'un pourcentage
 * Attendu ID : 634 | Niveau : 6ème | Domaine : NC (Nombres et calculs)
 *
 * Ce générateur contient 2 EXERCICES DIDACTIQUEMENT DISTINCTS (chacun avec
 * son propre generatorKey au niveau exercice), déclinés en plusieurs formats UI
 * pour un total de 6 entrées registry.
 *
 * Exercices couverts (voir FICHE_PEDAGO_CLUSTER19 §6.1 à §6.6) :
 *
 *   1. `convertir_pourcent_fraction` → QCM (% → frac), QCM (frac → %), Relier, Texte à trous.
 *   2. `representer_pourcentage`     → QCM, Saisie. Grille de 100 cases.
 *
 * Règles dures de la fiche (§3, §4) :
 *   - Pool de 21 pourcentages (1 % à 90 %) avec fractions simplifiées et décimaux.
 *   - Dénominateurs simplifiés ⊆ {2, 4, 5, 10, 20, 25, 50, 100}.
 *   - Distracteurs spécifiques : dénominateur comme %, fraction non simplifiée,
 *     numérateur faux, complément (100 % − a %), etc.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeTexteTrou, makeRelier,
} from '../../../utils';
import { gcd, simplify, fmtFrac, fmtKatex, fmtItem } from './fraction';

// ═══════════════════════════════════════════════════════════
// CONSTANTES & LABELS
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 634;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_CONVERTIR_QCMPCT  = 'Convertir un pourcentage en fraction simplifiée (QCM)';
const L_CONVERTIR_QCMFRAC = 'Convertir une fraction en pourcentage (QCM)';
const L_CONVERTIR_RELIER  = 'Convertir fractions et pourcentages (Relier)';
const L_CONVERTIR_TEXTE   = 'Compléter les écritures équivalentes (Texte à trous)';
const L_REPRESENTER_QCM   = 'Représenter un pourcentage sur une grille (QCM)';
const L_REPRESENTER_SAISIE = 'Représenter un pourcentage sur une grille (Saisie)';

// ═══════════════════════════════════════════════════════════
// POOL DE VALEURS (d'après fiche §3)
// ═══════════════════════════════════════════════════════════

/**
 * POOL — 21 couples (pourcentage, num/100, fraction simplifiée, décimal).
 * Tous les dénominateurs simplifiés divisent 100.
 */
const POOL = [
    { pct: 1,  num: 1,  den: 100, dec: '0,01' },
    { pct: 2,  num: 1,  den: 50,  dec: '0,02' },
    { pct: 4,  num: 1,  den: 25,  dec: '0,04' },
    { pct: 5,  num: 1,  den: 20,  dec: '0,05' },
    { pct: 8,  num: 2,  den: 25,  dec: '0,08' },
    { pct: 10, num: 1,  den: 10,  dec: '0,1' },
    { pct: 12, num: 3,  den: 25,  dec: '0,12' },
    { pct: 15, num: 3,  den: 20,  dec: '0,15' },
    { pct: 20, num: 1,  den: 5,   dec: '0,2' },
    { pct: 25, num: 1,  den: 4,   dec: '0,25' },
    { pct: 30, num: 3,  den: 10,  dec: '0,3' },
    { pct: 35, num: 7,  den: 20,  dec: '0,35' },
    { pct: 40, num: 2,  den: 5,   dec: '0,4' },
    { pct: 45, num: 9,  den: 20,  dec: '0,45' },
    { pct: 50, num: 1,  den: 2,   dec: '0,5' },
    { pct: 55, num: 11, den: 20,  dec: '0,55' },
    { pct: 60, num: 3,  den: 5,   dec: '0,6' },
    { pct: 70, num: 7,  den: 10,  dec: '0,7' },
    { pct: 75, num: 3,  den: 4,   dec: '0,75' },
    { pct: 80, num: 4,  den: 5,   dec: '0,8' },
    { pct: 90, num: 9,  den: 10,  dec: '0,9' },
];

// ═══════════════════════════════════════════════════════════
// EXERCICE 1 — convertir_pourcent_fraction
// ═══════════════════════════════════════════════════════════

/**
 * QCM : Pourcentage → Fraction simplifiée
 * Consigne : « Quel fraction simplifiée correspond à ce pourcentage ? »
 */
/** Ajoute un distracteur s'il est unique et différent de la bonne réponse. */
const addUnique = (set, val, correct) => {
    if (val !== correct) set.add(val);
};

const genConvertirQcmPourcent = () => {
    const item = randomChoice(POOL);
    const { pct, num, den } = item;
    const correct = fmtItem(num, den);

    // Distracteurs (fiche §4)
    const dSet = new Set();
    addUnique(dSet, fmtItem(num, pct), correct);             // dén comme %
    addUnique(dSet, fmtItem(pct, 100), correct);             // non simplifié
    const wrongNum = num === 1 ? 2 : num + 1;
    addUnique(dSet, fmtItem(wrongNum, den), correct);        // mauvais numérateur
    const complementItem = POOL.find(e => e.pct === 100 - pct);
    if (complementItem) addUnique(dSet, fmtItem(complementItem.num, complementItem.den), correct);
    // Fallback : fractions aléatoires du POOL
    let attempts = 0;
    while (dSet.size < 3 && attempts < 30) {
        const other = randomChoice(POOL);
        addUnique(dSet, fmtItem(other.num, other.den), correct);
        attempts++;
    }
    const choixList = [correct, ...[...dSet].slice(0, 3)];
    const texte = `Quelle fraction simplifiée correspond à ${pct} % ?`;

    const payload = makeQcm(choixList, correct, texte, opts(L_CONVERTIR_QCMPCT));
    payload._meta.generatorKey = 'convertir_pourcent_fraction';
    return payload;
};

/**
 * QCM : Fraction → Pourcentage
 * Consigne : « Quel pourcentage correspond à cette fraction ? »
 */
const genConvertirQcmFraction = () => {
    const item = randomChoice(POOL);
    const { pct, num, den } = item;
    const correct = `${pct} %`;

    const dSet = new Set();
    addUnique(dSet, `${den} %`, correct);               // dénominateur seul
    addUnique(dSet, `${num} %`, correct);               // numérateur seul
    addUnique(dSet, `${100 - pct} %`, correct);         // complément
    const neighbor = pct + (randInt(0, 1) === 0 ? 5 : -5);
    if (neighbor > 0) addUnique(dSet, `${neighbor} %`, correct);
    // Fallback : piocher dans le POOL
    let attempts = 0;
    while (dSet.size < 3 && attempts < 30) {
        const other = randomChoice(POOL);
        addUnique(dSet, `${other.pct} %`, correct);
        attempts++;
    }
    const choixList = [correct, ...[...dSet].slice(0, 3)];
    const texte = `Quel pourcentage correspond à {{element:0}}\u00a0?`;
    const exprEl = { version: 1, elements: [{ type: 'expression', config: { contenu: fmtKatex(num, den) } }] };

    const payload = makeQcm(choixList, correct, texte, { ...opts(L_CONVERTIR_QCMFRAC), elements_additionnels: exprEl });
    payload._meta.generatorKey = 'convertir_pourcent_fraction';
    return payload;
};

/**
 * Relier : 4 paires fraction ↔ pourcentage
 * Consigne : « Relie chaque fraction au pourcentage correspondant. »
 */
const genConvertirRelier = () => {
    // Piocher 4 éléments distincts du POOL
    const shuffled = shuffle(POOL);
    const selected = shuffled.slice(0, 4);

    const pairs = selected.map(item => ({
        source: fmtItem(item.num, item.den),
        target: `${item.pct} %`,
    }));

    const texte = `Relie chaque fraction au pourcentage correspondant.`;

    const payload = makeRelier(pairs, texte, opts(L_CONVERTIR_RELIER));
    payload._meta.generatorKey = 'convertir_pourcent_fraction';
    return payload;
};

/**
 * Texte à trous : Tableau d'écritures équivalentes
 * Consigne : « Complète les écritures équivalentes. »
 * Format : "a % = a/100 = p/q = d"
 */
const genConvertirTexte = () => {
    const item = randomChoice(POOL);
    const { pct, num, den, dec } = item;

    // Choisir 2 ou 3 trous aléatoirement (pas tous les 4)
    const nbTrous = randInt(2, 3);
    const indices = shuffle([0, 1, 2, 3]).slice(0, nbTrous);
    const hasIndex = (i) => indices.includes(i);

    // Construire la phrase KaTeX avec {{trous}}
    const parts = [
        hasIndex(0) ? `{{${pct}}}\\,\\%` : `${pct}\\,\\%`,
        hasIndex(1) ? `\\dfrac{{{${pct}}}}{100}` : `\\dfrac{${pct}}{100}`,
        hasIndex(2) ? `{{${fmtFrac(num, den)}}}` : fmtKatex(num, den),
        hasIndex(3) ? `{{${dec}}}` : dec,
    ];
    const phrase = `$${parts.join(' = ')}$`;

    const texte = `Complète les écritures équivalentes du pourcentage.`;

    const payload = makeTexteTrou(phrase, texte, { ...opts(L_CONVERTIR_TEXTE), mode: 'saisie', strict: false });
    payload._meta.generatorKey = 'convertir_pourcent_fraction';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 2 — representer_pourcentage
// ═══════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════
// SVG — Grille 10×10 pour représenter les pourcentages
// ═══════════════════════════════════════════════════════════

const SVG_STROKE = 'currentColor';
const SVG_CELL_EMPTY = 'rgba(255,255,255,0.08)';

/** Construit une grille 10×10 vide en SVG data URL. */
const buildGrille100Svg = () => {
    const cellSize = 28;
    const pad = 8;
    const gridSize = 10 * cellSize;
    const W = gridSize + 2 * pad;
    const H = gridSize + 2 * pad;
    const parts = [`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}">`];
    for (let r = 0; r < 10; r++) {
        for (let c = 0; c < 10; c++) {
            const x = pad + c * cellSize;
            const y = pad + r * cellSize;
            parts.push(`<rect x="${x}" y="${y}" width="${cellSize}" height="${cellSize}" fill="${SVG_CELL_EMPTY}" stroke="${SVG_STROKE}" stroke-width="1"/>`);
        }
    }
    parts.push('</svg>');
    return `data:image/svg+xml,${encodeURIComponent(parts.join(''))}`;
};

/** Helper elements_additionnels avec la grille 100 vide (SVG). */
const grilleElement = () => ({
    version: 1,
    elements: [{ type: 'image', config: { imageUrl: buildGrille100Svg(), maxWidth: 'compact' } }],
});

/** Dimensions (cols × rows) pour chaque grille diviseur de 100. */
const GRID_DIMENSIONS = {
    2: [2, 1],
    4: [2, 2],
    5: [5, 1],
    10: [5, 2],
    20: [5, 4],
    25: [5, 5],
    50: [10, 5],
    100: [10, 10],
};

/** Construit une grille N cases avec `coloredCount` cases coloriées. */
const buildGrilleCustomSvg = (N, coloredCount) => {
    const [cols, rows] = GRID_DIMENSIONS[N];
    const cellSize = 28;
    const pad = 8;
    const W = cols * cellSize + 2 * pad;
    const H = rows * cellSize + 2 * pad;
    const parts = [`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}">`];
    let idx = 0;
    for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
            const x = pad + c * cellSize;
            const y = pad + r * cellSize;
            const fill = idx < coloredCount ? '#60a5fa' : SVG_CELL_EMPTY;
            parts.push(`<rect x="${x}" y="${y}" width="${cellSize}" height="${cellSize}" fill="${fill}" stroke="${SVG_STROKE}" stroke-width="1"/>`);
            idx++;
        }
    }
    parts.push('</svg>');
    return `data:image/svg+xml,${encodeURIComponent(parts.join(''))}`;
};

const grilleCustomElement = (N, coloredCount) => ({
    version: 1,
    elements: [{ type: 'image', config: { imageUrl: buildGrilleCustomSvg(N, coloredCount), maxWidth: 'compact' } }],
});

// ─── Helpers partagés pour Representer (QCM + Saisie) ───

/** Mode A : pct aléatoire ∈ [1, 99], grille 100 vide. */
const genRepresenterModeA = () => ({ pct: randInt(1, 99) });

/** Mode B : grille N avec C cases colorées (N diviseur de 100, inclut 100). */
const genRepresenterModeB = () => {
    const N = randomChoice([4, 5, 10, 20, 25, 50, 100]);
    const C = randInt(1, N - 1);
    const pct = (100 * C) / N;  // toujours entier par construction
    return { N, C, pct };
};

/**
 * QCM : représenter un pourcentage sur une grille (50% mode A / 50% mode B).
 */
const genRepresenterQcm = () => {
    const modeA = Math.random() < 0.5;
    if (modeA) {
        // Mode A : grille 100 vide, "combien de cases pour X %"
        const { pct } = genRepresenterModeA();
        const correct = `${pct}`;
        const dSet = new Set();
        addUnique(dSet, `${pct * 10}`, correct);
        if (pct >= 10) addUnique(dSet, `${pct / 10}`, correct);
        addUnique(dSet, `${100 - pct}`, correct);
        addUnique(dSet, `${pct + 5}`, correct);
        addUnique(dSet, `${pct + 10}`, correct);
        let attempts = 0;
        while (dSet.size < 3 && attempts < 20) {
            addUnique(dSet, `${randInt(1, 99)}`, correct);
            attempts++;
        }
        const choixList = [correct, ...[...dSet].slice(0, 3)];
        const texte = `<p>Sur une grille de 100 cases, combien de cases faut-il colorier pour représenter ${pct} %\u00a0?</p>{{element:0}}`;
        const payload = makeQcm(choixList, correct, texte, { ...opts(L_REPRESENTER_QCM), elements_additionnels: grilleElement() });
        payload._meta.generatorKey = 'representer_pourcentage';
        return payload;
    }
    // Mode B : grille N avec C cases colorées, "quel %"
    const { N, C, pct } = genRepresenterModeB();
    const correct = `${pct}`;
    const dSet = new Set();
    addUnique(dSet, `${C}`, correct);         // piège "confond cases et %"
    addUnique(dSet, `${N - C}`, correct);     // piège "cases non colorées"
    addUnique(dSet, `${100 - pct}`, correct); // piège "complément à 100"
    addUnique(dSet, `${pct + 5}`, correct);   // voisin
    addUnique(dSet, `${pct - 5}`, correct);   // voisin
    let attempts = 0;
    while (dSet.size < 3 && attempts < 20) {
        addUnique(dSet, `${randInt(1, 99)}`, correct);
        attempts++;
    }
    const choixList = [correct, ...[...dSet].slice(0, 3)];
    const texte = `<p>Quel pourcentage représente la partie coloriée de cette grille de ${N} cases\u00a0?</p>{{element:0}}`;
    const payload = makeQcm(choixList, correct, texte, { ...opts(L_REPRESENTER_QCM), elements_additionnels: grilleCustomElement(N, C) });
    payload._meta.generatorKey = 'representer_pourcentage';
    return payload;
};

/**
 * Saisie : représenter un pourcentage sur une grille (50% mode A / 50% mode B).
 */
const genRepresenterSaisie = () => {
    const modeA = Math.random() < 0.5;
    if (modeA) {
        const { pct } = genRepresenterModeA();
        const payload = makeSaisie(`${pct}`, `<p>À ${pct} %, combien de cases sur 100 doivent être coloriées\u00a0?</p>{{element:0}}`, {
            ...opts(L_REPRESENTER_SAISIE),
            suffixe: 'cases',
            elements_additionnels: grilleElement(),
        });
        payload._meta.generatorKey = 'representer_pourcentage';
        return payload;
    }
    const { N, C, pct } = genRepresenterModeB();
    const texte = `<p>Quel pourcentage représente la partie coloriée de cette grille de ${N} cases\u00a0?</p>{{element:0}}`;
    const payload = makeSaisie(`${pct}`, texte, {
        ...opts(L_REPRESENTER_SAISIE),
        suffixe: '%',
        elements_additionnels: grilleCustomElement(N, C),
    });
    payload._meta.generatorKey = 'representer_pourcentage';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    convertir_pourcent_fraction_qcm_pourcent: genConvertirQcmPourcent,
    convertir_pourcent_fraction_qcm_fraction: genConvertirQcmFraction,
    convertir_pourcent_fraction_relier:       genConvertirRelier,
    convertir_pourcent_fraction_texte_trou:   genConvertirTexte,
    representer_pourcentage_qcm:             genRepresenterQcm,
    representer_pourcentage_saisie:          genRepresenterSaisie,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_saisie: 'Saisie trous', texte_trou_drag: 'Drag',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    {
        id: 'convertir_pourcent_fraction',
        label: 'Convertir pourcentages et fractions',
        formatsList: buildFormats('convertir_pourcent_fraction'),
    },
    {
        id: 'representer_pourcentage',
        label: 'Représenter un pourcentage sur grille',
        formatsList: buildFormats('representer_pourcentage'),
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    POOL, gcd, simplify, fmtFrac, fmtKatex, fmtItem,
    genConvertirQcmPourcent, genConvertirQcmFraction,
    genConvertirRelier, genConvertirTexte,
    genRepresenterQcm, genRepresenterSaisie,
};
